import React, { useCallback, useEffect, useRef } from 'react';
import type { FloatingButton } from 'shared/src/api/promotionsService/getPromotionComponents/types';
import {
  BadgeNew,
  Colors,
  Flex,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import {
  getRemainTime,
  getRemainTimeUnit,
  isNil,
  isNotNil,
  localStorageBridge,
} from 'shared/src/utils';
import { useOpenDeeplink } from 'shared/src/hooks';
import { useClickFloatingButton } from '../hooks/useClickFloatingButton';

const BUTTON_SIZE = 48;

const FLOATING_BUTTON_NEW_BADGE_SHOW_LOCAL_KEY =
  `FLOATING_BUTTON_NEW_BADGE_SHOW_LOCAL_KEY_` as const;

export const PromotionFloatingButton: React.FC<{
  button: FloatingButton;
  buttonIdx: number;
  promotionSno?: number;
}> = props => {
  const repeatRef = useRef<number | null>(null);
  const remainTimeRef = useRef<number | null>(null);
  const [isClickOnce, setIsClickOnce] = React.useState(false);
  const [isShowText, setIsShowText] = React.useState(true);
  const [remainTime, setRemainTime] = React.useState('');
  const { button, buttonIdx, promotionSno } = props;
  const { openDeeplink } = useOpenDeeplink();
  const logClickFloatingButton = useClickFloatingButton(
    props.button,
    buttonIdx,
  );
  // new 여부는 localstorage에 저장하기
  // 클릭시 로깅 추가
  const onPressButton = useCallback(() => {
    logClickFloatingButton();
    setIsClickOnce(true);

    localStorageBridge.putBoolean(
      `${FLOATING_BUTTON_NEW_BADGE_SHOW_LOCAL_KEY}${promotionSno}`,
      true,
    );

    openDeeplink(button.item.deeplink);
  }, [button.item.deeplink, logClickFloatingButton, promotionSno, openDeeplink]);

  const checkClickOncePromotion = useCallback(async () => {
    if (isNil(promotionSno)) {
      return;
    }
    const value = isNotNil(
      await localStorageBridge.getBoolean(
        `${FLOATING_BUTTON_NEW_BADGE_SHOW_LOCAL_KEY}${promotionSno}`,
      ),
    );
    setIsClickOnce(value);
  }, [promotionSno]);

  useEffect(() => {
    checkClickOncePromotion();
  }, [checkClickOncePromotion]);

  useEffect(() => {
    if (isNotNil(button.item.render.timer)) {
      repeatRef.current = setInterval(() => {
        setIsShowText(prev => !prev);
      }, 3000);

      const remainTimeUnit = getRemainTimeUnit(
        button.item.render.timer.expired_at,
      );
      const remainTime = getRemainTime(button.item.render.timer.expired_at);
      setRemainTime(`${remainTime} 남음`);

      const getRepeatTime = () => {
        switch (remainTimeUnit) {
          // //일주일 이상은 안돌림
          // case 'week': return null;
          // //하루 이상도 안돌림
          // case 'day': return null;
          // case 'hour':
          //   return 1000 * 60 * 60;
          case 'minute':
            return 1000 * 60;
          case 'second':
            return 1000;
          //일주일, 하루 이상인 경우에는 interval 안돌리도록
          default:
            return null;
        }
      };

      const repeatTime = getRepeatTime();
      if (isNotNil(repeatTime)) {
        remainTimeRef.current = setInterval(() => {
          if (isNotNil(button.item.render.timer)) {
            const remainTime = getRemainTime(
              button.item.render.timer.expired_at,
            );
            setRemainTime(`${remainTime} 남음`);
          }
        }, repeatTime);
      }
    }
    return () => {
      if (isNotNil(repeatRef.current)) {
        clearInterval(repeatRef.current);
        repeatRef.current = null;
      }

      if (isNotNil(remainTimeRef.current)) {
        clearInterval(remainTimeRef.current);
        remainTimeRef.current = null;
      }
    };
  }, [button.item.render.timer]);

  return (
    <Flex
      width={BUTTON_SIZE}
      height={BUTTON_SIZE}
      borderRadius={16}
      borderWidth={1}
      alignItems={'center'}
      justifyContent={'center'}
      shadowStep={'medium'}
      borderColorValue={
        button.item.render.border_color ?? Colors.border_secondary
      }
      backgroundColorValue={button.item.render.background_color}
      onPress={onPressButton}
    >
      <RemoteImage uri={button.item.render.icon} width={20} height={20} />
      {!isShowText && isNotNil(button.item.render.timer) ? (
        <Typography
          type={'subtitle7'}
          numberOfLines={1}
          rawColor={button.item.render.timer.text_color}
        >
          {remainTime}
        </Typography>
      ) : (
        <Typography
          type={'subtitle7'}
          numberOfLines={1}
          rawColor={button.item.render.text_color}
        >
          {button.item.render.text}
        </Typography>
      )}
      {!isClickOnce && (
        <Flex position={'absolute'} top={0} right={0}>
          <BadgeNew />
        </Flex>
      )}
    </Flex>
  );
};
